import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PlaybookCard from '../components/PlaybookCard';
import GrowthPlaybooksBanner from '../components/GrowthPlaybooksBanner';
import { getPlaybookStage } from '../constants/playbookStageFilters';
import CompanyLogosSection from '../components/CompanyLogosSection';
import { useCourses, CourseWithProgress } from '@/hooks/useCourses';
import { Spinner } from '@/components/ui/loading-spinner';

// Helper function to get relevant skills based on playbook title
const getRelevantSkills = (title: string): string[] => {
  const titleLower = title.toLowerCase();
  
  if (titleLower.includes('first 20 users') || titleLower.includes('get your first')) {
    return [
      'Profile optimization',
      'Community outreach',
      'Social proof creation',
      'Direct messaging'
    ];
  } else if (titleLower.includes('comments & dms') || titleLower.includes('commenting and dm') || titleLower.includes('cold outreach')) {
    return [
      'Sales psychology',
      'Conversion messaging',
      'User feedback analysis',
      'Referral systems'
    ];
  } else if (titleLower.includes('build in public') || titleLower.includes('building momentum')) {
    return [
      'Build-in-public strategy',
      'Audience engagement',
      'Content distribution',
      'Community building'
    ];
  } else if (titleLower.includes('organic growth') || titleLower.includes('blogs')) {
    return [
      'SEO blog writing',
      'Content marketing',
      'Organic traffic growth',
      'Social media posting'
    ];
  } else if (titleLower.includes('lead magnet') || titleLower.includes('email sequence')) {
    return [
      'Lead magnet creation',
      'Email automation',
      'Landing page optimization',
      'Conversion funnels'
    ];
  } else if (titleLower.includes('viral') && titleLower.includes('video')) {
    return [
      'Video content creation',
      'UGC collection',
      'Viral video strategies',
      'Platform optimization'
    ];
  } else if (titleLower.includes('advanced scaling') || titleLower.includes('partnership')) {
    return [
      'Meta ads management',
      'Performance marketing',
      'Partnership development',
      'Scaling strategies'
    ];
  } else if (titleLower.includes('retention') || titleLower.includes('revenue per user')) {
    return [
      'Customer retention',
      'Revenue optimization',
      'User lifecycle management',
      'Upselling techniques'
    ];
  } else if (titleLower.includes('viral launch') || titleLower.includes('launch strategies')) {
    return [
      'Launch campaign planning',
      'Product Hunt optimization',
      'PR & media outreach',
      'Launch distribution'
    ];
  }
  
  // Default skills if none of the specific titles match
  return [
    'Growth strategy',
    'User acquisition',
    'Content creation',
    'Analytics interpretation'
  ];
};

const CoursesListPage = () => {
  const navigate = useNavigate();
  const { id: projectId } = useParams<{ id: string }>();
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const { useGetCourses } = useCourses();
  const { data: coursesDataWithProgress, isLoading: isLoadingCourses, error: coursesError } = useGetCourses();
  
  // Adapt data for PlaybookCard
  const coursesForCard = (coursesDataWithProgress || []).map(course => ({
    id: course.id,
    title: course.title,
    short_description: course.shortDescription,
    badge: undefined, // Remove all badges for cleaner design
    card_image_url: course.cardImage,
    detail_image_url: course.detailImage,
    outcomes: course.outcomes || [],
    // Apply relevant skills based on the playbook title
    new_skills: getRelevantSkills(course.title),
    is_published: (course as any).is_published !== undefined ? (course as any).is_published : true,
    display_order: (course as any).display_order !== undefined ? (course as any).display_order : 0,
    introduction: course.introduction,
    user_progress: course.userProgress ? {
        completed_task_ids: course.userProgress.completed_task_ids || [],
        current_phase_id: course.userProgress.current_phase_id || undefined,
    } : undefined,
    stage: getPlaybookStage(course.title), // Add stage information
  }));
  
  const filteredCourses = coursesForCard.filter(course => {
    if (!activeFilter) return true;
    return course.stage === activeFilter;
  });
  
  const handlePlaybookClick = (playbookId: string) => {
    navigate(`/projects/${projectId}/playbooks/${playbookId}`); // Path might need update if "playbooks" becomes "courses"
  };
  
  return (
    <div className="w-full px-3 sm:px-6 py-4 sm:py-6 bg-white dark:bg-zinc-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Mobile-optimized company logos */}
        <div className="mb-4 sm:mb-6">
          <CompanyLogosSection appearance="darkBar" />
        </div>
        
        {/* Mobile-optimized growth playbooks banner */}
        <div className="mb-6 sm:mb-8">
          <GrowthPlaybooksBanner activeFilter={activeFilter} onFilterChange={setActiveFilter} />
        </div>
        
        <div className="flex flex-col space-y-4 sm:space-y-6">
          {isLoadingCourses ? (
            <div className="min-h-[200px] flex justify-center items-center">
              <Spinner className="h-8 w-8 text-blue-500 dark:text-blue-400" />
            </div>
          ) : coursesError ? (
             <div className="text-center py-8 sm:py-12 bg-white dark:bg-zinc-800 rounded-lg border border-zinc-200 dark:border-zinc-700 mx-2 sm:mx-0">
              <h3 className="text-lg font-semibold mb-2 text-red-600 dark:text-red-400">Error loading playbooks</h3>
              <p className="text-gray-500 dark:text-zinc-400 px-4">{coursesError.message || 'Could not fetch playbook data.'}</p>
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="text-center py-8 sm:py-12 bg-white dark:bg-zinc-800 rounded-lg border border-zinc-200 dark:border-zinc-700 mx-2 sm:mx-0">
              <h3 className="text-lg font-semibold mb-2 text-zinc-900 dark:text-zinc-100">No playbooks found</h3>
              <p className="text-gray-500 dark:text-zinc-400 px-4">Try selecting a different stage</p>
            </div>
          ) : (
            /* Mobile-optimized grid: Single column on mobile, responsive on larger screens */
            <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 px-2 sm:px-0">
              {filteredCourses.map((courseMapped, index) => (
                <PlaybookCard
                  key={courseMapped.id}
                  playbook={courseMapped}
                  onClick={() => handlePlaybookClick(courseMapped.id)}
                  index={index}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoursesListPage; 